import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { AlertCircle } from 'lucide-react';

interface EditPriorityDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  member: {
    id: string;
    bot_id: string;
    priority: number;
    group_id: string;
    bots?: {
      name: string;
    };
  } | null;
  existingPriorities: number[];
  onSuccess: () => void;
}

export const EditPriorityDialog: React.FC<EditPriorityDialogProps> = ({
  open,
  onOpenChange,
  member,
  existingPriorities,
  onSuccess,
}) => {
  const [newPriority, setNewPriority] = useState<number>(1);
  const [error, setError] = useState<string>('');
  const queryClient = useQueryClient();

  useEffect(() => {
    if (member) {
      setNewPriority(member.priority);
      setError('');
    }
  }, [member]);

  const updatePriorityMutation = useMutation({
    mutationFn: async (priority: number) => {
      if (!member) throw new Error('No member selected');

      // Check if priority already exists (excluding current member)
      const priorityExists = existingPriorities.some(
        (p) => p === priority && p !== member.priority
      );

      if (priorityExists) {
        throw new Error(`Priority ${priority} is already assigned to another bot`);
      }

      // Step 1: Update priority
      // @ts-ignore - strategy_group_members table
      const { error } = await supabase
        // @ts-ignore
        .from('strategy_group_members')
        .update({
          priority,
          updated_at: new Date().toISOString(),
        })
        .eq('id', member.id);

      if (error) throw error;
      
      // Step 2: Trigger recalculation after priority change
      const { error: recalcError } = await supabase.functions.invoke(
        'conditional-skip',
        {
          body: { groupId: member.group_id }
        }
      );
      
      if (recalcError) {
        console.error('Failed to recalculate active bot:', recalcError);
        // Don't throw - priority was updated successfully
      }
    },
    onSuccess: () => {
      toast.success('Priority updated and group recalculated');
      queryClient.invalidateQueries({ queryKey: ['strategy-groups'] });
      onSuccess();
      onOpenChange(false);
    },
    onError: (error: Error) => {
      setError(error.message);
      toast.error(error.message);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (newPriority < 1) {
      setError('Priority must be at least 1');
      return;
    }

    if (newPriority === member?.priority) {
      onOpenChange(false);
      return;
    }

    updatePriorityMutation.mutate(newPriority);
  };

  // Find suggested next priority
  const suggestedPriority = Math.max(...existingPriorities, 0) + 1;

  if (!member) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit Priority</DialogTitle>
          <DialogDescription>
            Change the priority for <strong>{member.bots?.name || 'Unknown Bot'}</strong>
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          <div className="space-y-4 py-4">
            {/* Current Priority */}
            <div className="space-y-2">
              <Label>Current Priority</Label>
              <Badge variant="secondary" className="text-base px-3 py-1">
                P{member.priority}
              </Badge>
            </div>

            {/* New Priority Input */}
            <div className="space-y-2">
              <Label htmlFor="new-priority">
                New Priority <span className="text-destructive">*</span>
              </Label>
              <Input
                id="new-priority"
                type="number"
                min="1"
                value={newPriority}
                onChange={(e) => {
                  setNewPriority(parseInt(e.target.value) || 1);
                  setError('');
                }}
                className={error ? 'border-destructive' : ''}
              />
              {!error && newPriority !== member.priority && (
                <p className="text-xs text-muted-foreground">
                  Suggested next available: P{suggestedPriority}
                </p>
              )}
            </div>

            {/* Error Message */}
            {error && (
              <div className="flex items-start gap-2 p-3 bg-destructive/10 border border-destructive rounded-md">
                <AlertCircle className="h-5 w-5 text-destructive flex-shrink-0 mt-0.5" />
                <p className="text-sm text-destructive">{error}</p>
              </div>
            )}

            {/* Info: Existing Priorities */}
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">Existing Priorities</Label>
              <div className="flex flex-wrap gap-2">
                {existingPriorities
                  .filter((p) => p !== member.priority)
                  .sort((a, b) => a - b)
                  .map((p) => (
                    <Badge key={p} variant="outline">
                      P{p}
                    </Badge>
                  ))}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={updatePriorityMutation.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={updatePriorityMutation.isPending}>
              {updatePriorityMutation.isPending ? 'Updating...' : 'Update Priority'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
